import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

// const DEFAULT_CHAT_IFRAME_URL = 'http://192.168.56.1:3000/';
const DEFAULT_CHAT_IFRAME_URL = 'https://medo-52425.web.app';

export function ChatEmbedView({ onBack }: { onBack?: () => void }) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [isFrameReady, setIsFrameReady] = useState(false);

  const iframeUrl = (DEFAULT_CHAT_IFRAME_URL).trim();
  const iframeOrigin = useMemo(() => {
    try {
      return new URL(iframeUrl).origin;
    } catch {
      return '';
    }
  }, [iframeUrl]);

  const postSessionToFrame = useCallback((session: Session | null) => {
    if (!session || !iframeOrigin) return;
    const targetWindow = frameRef.current?.contentWindow;
    if (!targetWindow) return;

    targetWindow.postMessage(
      {
        type: 'SUPABASE_TOKEN',
        token: session.access_token,
        refresh_token: session.refresh_token,
      },
      iframeOrigin
    );
  }, [iframeOrigin]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return;
    if (!isFrameReady) return;

    let isActive = true;

    const sendCurrentSession = async () => {
      const { getCurrentSession } = await import('@/lib/supabase');
      const session = await getCurrentSession();
      if (!isActive) return;
      postSessionToFrame(session);
    };

    sendCurrentSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isActive) return;
      postSessionToFrame(session);
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, [isFrameReady, postSessionToFrame]);

  return (
    <div className="flex h-full flex-col bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-900 px-4 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-gray-900 dark:text-white">Your chats</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Stay connected in real time</p>
          </div>
        </div>
      </div>

      {!iframeOrigin && (
        <div className="px-4 py-6 text-sm text-red-600 dark:text-red-400">
          Invalid chat iframe URL. Check VITE_CHAT_IFRAME_URL.
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <iframe
          ref={frameRef}
          id="convaytoFrame"
          src={iframeUrl}
          title="Chats"
          className="w-full h-full border-0"
          onLoad={() => setIsFrameReady(true)}
        />
      </div>
    </div>
  );
}
